import * as Sentry from '@sentry/capacitor';
import { Capacitor } from '@capacitor/core';

const isTrue = (value: string | undefined, fallback = false): boolean => {
  if (value === undefined) return fallback;
  return ['1', 'true', 'yes'].includes(value.toLowerCase());
};

const runtimeEnv = import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE || 'development';
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

const sentryEnabled = isTrue(import.meta.env.VITE_SENTRY_ENABLED, true);

const parseUser = (): Record<string, unknown> | null => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

export const attachUserContext = async (): Promise<void> => {
  const user = parseUser();
  const userId = user?.id ? String(user.id) : undefined;
  if (userId) {
    await Sentry.setUser({
      id: userId,
      email: typeof user?.email === 'string' ? user.email : undefined,
      username: typeof user?.name === 'string' ? user.name : undefined,
    });
  } else {
    await Sentry.setUser(null);
  }
};

export const initObservability = async (): Promise<void> => {
  if (sentryEnabled && sentryDsn) {
    await Sentry.init({
      dsn: sentryDsn,
      environment: runtimeEnv,
      release: import.meta.env.VITE_SENTRY_RELEASE || 'dev',
      enableNative: true,
      enableAutoSessionTracking: true,
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
      beforeSend(event) {
        if (event.request?.headers) {
          delete event.request.headers.Authorization;
          delete event.request.headers.authorization;
          delete event.request.headers.Cookie;
          delete event.request.headers.cookie;
        }
        return event;
      },
    });
    await Sentry.setTag('service', 'contractor-app');
    await Sentry.setTag('platform', Capacitor.getPlatform());
    await Sentry.setContext('device', {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
    });
    await Sentry.addBreadcrumb({
      category: 'app.lifecycle',
      level: 'info',
      message: 'Contractor app booted',
      data: {
        platform: Capacitor.getPlatform(),
      },
    });
  }

  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason);
  });

  await attachUserContext();
};

export const captureApiFailure = (error: unknown, context: Record<string, unknown>): void => {
  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('source', 'axios');
    Object.entries(context).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        scope.setContext(key, typeof value === 'object' ? (value as object) : { value });
      }
    });
    Sentry.captureException(error);
  });
};
